'use client'

import { useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useMaintenanceCountdown } from '../../hooks/useMaintenanceCountdown'
import {
  MAINTENANCE_STATE_QUERY_KEY,
  useMaintenanceState
} from '../../hooks/useMaintenanceState'
import { formatCountdown } from '../../lib/formatCountdown'
import { formatReturnTime } from '../../lib/formatReturnTime'

import { useAppRouter, useAppSearchParams } from '@/shared/lib/router'
import { StatusScreen } from '@/shared/ui/StatusScreen'

// Para onde devolver o aluno quando a janela acabar, se a rota de origem não
// veio. Nunca `/`: a landing é a tela de login, e cair nela depois da
// manutenção parece que a sessão foi perdida.
const FALLBACK_ROUTE = '/jornadas'

export const MaintenancePage = () => {
  const router = useAppRouter()
  const searchParams = useAppSearchParams()
  const queryClient = useQueryClient()
  const { data: maintenance } = useMaintenanceState()

  const onElapsed = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: MAINTENANCE_STATE_QUERY_KEY })
  }, [queryClient])

  const remainingMs = useMaintenanceCountdown(
    maintenance?.ends_at ?? null,
    onElapsed
  )

  // A janela terminou: devolve o aluno à rota em que ele estava, com a sessão
  // intacta. Sem isto ele voltaria para a landing e teria de entrar de novo.
  useEffect(() => {
    if (!maintenance || maintenance.active) return

    const from = searchParams.get('from')
    router.replace(from && from !== '/' ? from : FALLBACK_ROUTE)
  }, [maintenance, router, searchParams])

  const returnTime = formatReturnTime(maintenance?.ends_at ?? null)

  return (
    <StatusScreen
      variant="lightBlue"
      illustrationSrc="/misc/robot-cool.png"
      title="Já já a gente volta!"
      description="A plataforma está passando por uma manutenção rápida. Não precisa fazer nada — assim que terminar, você volta de onde parou."
    >
      {maintenance?.ends_at && (
        <div className="flex flex-col items-center gap-1">
          {/* font-hl é o token de número/label gamificada curta do DS. */}
          <span
            className="text-primary-dark font-hl text-4xl font-bold tabular-nums"
            role="timer"
            aria-live="off"
          >
            {formatCountdown(remainingMs)}
          </span>
          {returnTime && (
            <span className="text-american-blue/70 text-sm">
              Voltamos às {returnTime}
            </span>
          )}
        </div>
      )}
    </StatusScreen>
  )
}
